import { useMemo, useState, type ReactElement } from 'react';
import { X } from 'lucide-react';
import type {
    JsonValue,
    SearchFieldPredicate,
    SearchFieldPredicateValue,
    SearchJsonPathPredicate,
} from '@pillar-portal/api-types';
import { JsonValueSchema } from '@pillar-portal/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListJsonPathPredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';

const NoFieldValue = '__no_field_predicate__';
const NoJsonFieldValue = '__no_json_path_predicate__';

export interface ListPredicateControlsValue {
    readonly fieldPredicates: ReadonlyArray<SearchFieldPredicate>;
    readonly jsonPathPredicates: ReadonlyArray<SearchJsonPathPredicate>;
}

export interface ListPredicateControlsProps {
    readonly fieldPredicateDescriptors?: ReadonlyArray<
        InfiniteScrollListFieldPredicateDescriptor
    >;
    readonly jsonPathPredicateDescriptors?: ReadonlyArray<
        InfiniteScrollListJsonPathPredicateDescriptor
    >;
    readonly onChange: (value: ListPredicateControlsValue) => void;
}

interface PredicateChipProps {
    readonly label: string;
    readonly removeLabel: string;
    readonly onRemove: () => void;
}

function PredicateChip({
    label,
    removeLabel,
    onRemove,
}: PredicateChipProps): ReactElement {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900">
            <span>{label}</span>
            <button
                type="button"
                aria-label={removeLabel}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={onRemove}
            >
                <X className="h-3 w-3" aria-hidden="true" />
            </button>
        </span>
    );
}

function parseTypedValue(
    rawValue: string,
    descriptor: InfiniteScrollListFieldPredicateDescriptor
): SearchFieldPredicateValue {
    if (descriptor.valueType === 'number') {
        const parsedNumber = Number(rawValue);
        if (!Number.isFinite(parsedNumber)) {
            throw new Error(`Invalid numeric predicate value: ${rawValue}`);
        }
        return parsedNumber;
    }
    if (descriptor.valueType === 'boolean') {
        return rawValue === 'true';
    }
    return rawValue;
}

function parseJsonValue(rawValue: string): JsonValue {
    const trimmedValue = rawValue.trim();
    if (trimmedValue.length === 0) {
        return '';
    }
    try {
        return JsonValueSchema.parse(JSON.parse(trimmedValue));
    } catch {
        return trimmedValue;
    }
}

function splitJsonPath(rawValue: string): string[] {
    return rawValue
        .split('.')
        .map((pathPart: string) => pathPart.trim())
        .filter((pathPart: string) => pathPart.length > 0);
}

export function ListPredicateControls({
    fieldPredicateDescriptors = [],
    jsonPathPredicateDescriptors = [],
    onChange,
}: ListPredicateControlsProps): ReactElement | null {
    const [fieldPredicates, setFieldPredicates] = useState<
        ReadonlyArray<SearchFieldPredicate>
    >([]);
    const [jsonPathPredicates, setJsonPathPredicates] = useState<
        ReadonlyArray<SearchJsonPathPredicate>
    >([]);
    const [selectedField, setSelectedField] = useState<string>(NoFieldValue);
    const [selectedJsonField, setSelectedJsonField] =
        useState<string>(NoJsonFieldValue);
    const [jsonPathInput, setJsonPathInput] = useState<string>('');
    const [jsonValueInput, setJsonValueInput] = useState<string>('');

    const selectedFieldDescriptor:
        | InfiniteScrollListFieldPredicateDescriptor
        | undefined = useMemo(
        () =>
            fieldPredicateDescriptors.find(
                (descriptor) => descriptor.field === selectedField
            ),
        [fieldPredicateDescriptors, selectedField]
    );
    const selectedJsonDescriptor:
        | InfiniteScrollListJsonPathPredicateDescriptor
        | undefined = useMemo(
        () =>
            jsonPathPredicateDescriptors.find(
                (descriptor) => descriptor.field === selectedJsonField
            ),
        [jsonPathPredicateDescriptors, selectedJsonField]
    );

    const publish = (
        nextFieldPredicates: ReadonlyArray<SearchFieldPredicate>,
        nextJsonPathPredicates: ReadonlyArray<SearchJsonPathPredicate>
    ): void => {
        onChange({
            fieldPredicates: nextFieldPredicates,
            jsonPathPredicates: nextJsonPathPredicates,
        });
    };

    const setFieldPredicate = (
        predicate: SearchFieldPredicate | null,
        field: string
    ): void => {
        const nextFieldPredicates = [
            ...fieldPredicates.filter((currentPredicate) => {
                return currentPredicate.field !== field;
            }),
            ...(predicate === null ? [] : [predicate]),
        ];
        setFieldPredicates(nextFieldPredicates);
        publish(nextFieldPredicates, jsonPathPredicates);
    };

    const addJsonPathPredicate = (): void => {
        if (selectedJsonDescriptor === undefined) {
            return;
        }
        const path = splitJsonPath(jsonPathInput);
        if (path.length === 0) {
            return;
        }
        const nextPredicate: SearchJsonPathPredicate = {
            field: selectedJsonDescriptor.field,
            path,
            operator: 'eq',
            value: parseJsonValue(jsonValueInput),
        };
        const nextJsonPathPredicates = [
            ...jsonPathPredicates,
            nextPredicate,
        ];
        setJsonPathPredicates(nextJsonPathPredicates);
        setJsonPathInput('');
        setJsonValueInput('');
        publish(fieldPredicates, nextJsonPathPredicates);
    };

    const removeJsonPathPredicate = (index: number): void => {
        const nextJsonPathPredicates = jsonPathPredicates.filter((_, currentIndex) => {
            return currentIndex !== index;
        });
        setJsonPathPredicates(nextJsonPathPredicates);
        publish(fieldPredicates, nextJsonPathPredicates);
    };

    if (
        fieldPredicateDescriptors.length === 0 &&
        jsonPathPredicateDescriptors.length === 0
    ) {
        return null;
    }

    return (
        <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-3">
            {fieldPredicateDescriptors.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        aria-label="Predicate field"
                        className="h-9 min-w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        value={selectedField}
                        onChange={(event) => setSelectedField(event.target.value)}
                    >
                        <option value={NoFieldValue}>Field filter</option>
                        {fieldPredicateDescriptors.map((descriptor) => (
                            <option key={descriptor.field} value={descriptor.field}>
                                {descriptor.label}
                            </option>
                        ))}
                    </select>
                    {selectedFieldDescriptor !== undefined ? (
                        <select
                            aria-label={`${selectedFieldDescriptor.label} value`}
                            className="h-9 min-w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            value=""
                            onChange={(event) => {
                                const selectedValue = event.target.value;
                                if (selectedValue.length === 0) {
                                    setFieldPredicate(
                                        null,
                                        selectedFieldDescriptor.field
                                    );
                                    return;
                                }
                                const typedValue = parseTypedValue(
                                    selectedValue,
                                    selectedFieldDescriptor
                                );
                                setFieldPredicate(
                                    selectedFieldDescriptor.operator === 'eq'
                                        ? {
                                              field: selectedFieldDescriptor.field,
                                              operator: 'eq',
                                              value: typedValue,
                                          }
                                        : (() => {
                                              const currentPredicate =
                                                  fieldPredicates.find(
                                                      (predicate) =>
                                                          predicate.field ===
                                                          selectedFieldDescriptor.field
                                                  );
                                              const currentValues =
                                                  currentPredicate?.operator === 'in'
                                                      ? currentPredicate.values ?? []
                                                      : [];
                                              const nextValues = currentValues.some(
                                                  (currentValue) =>
                                                      currentValue === typedValue
                                              )
                                                  ? currentValues
                                                  : [...currentValues, typedValue];
                                              return {
                                                  field: selectedFieldDescriptor.field,
                                                  operator: 'in',
                                                  values: nextValues,
                                              };
                                          })(),
                                    selectedFieldDescriptor.field
                                );
                            }}
                        >
                            <option value="">All values</option>
                            {selectedFieldDescriptor.options.map((option) => (
                                <option
                                    key={`${selectedFieldDescriptor.field}:${String(option.value)}`}
                                    value={String(option.value)}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                        {fieldPredicates.map((predicate) => (
                            <PredicateChip
                                key={predicate.field}
                                label={`${predicate.field}: ${
                                    predicate.operator === 'eq'
                                        ? String(predicate.value)
                                        : predicate.values?.map(String).join(', ') ?? ''
                                }`}
                                removeLabel={`Remove ${predicate.field} filter`}
                                onRemove={() =>
                                    setFieldPredicate(null, predicate.field)
                                }
                            />
                        ))}
                    </div>
                </div>
            ) : null}

            {jsonPathPredicateDescriptors.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        aria-label="JSON predicate field"
                        className="h-9 min-w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        value={selectedJsonField}
                        onChange={(event) => setSelectedJsonField(event.target.value)}
                    >
                        <option value={NoJsonFieldValue}>JSON field</option>
                        {jsonPathPredicateDescriptors.map((descriptor) => (
                            <option key={descriptor.field} value={descriptor.field}>
                                {descriptor.label}
                            </option>
                        ))}
                    </select>
                    <Input
                        aria-label="JSON path"
                        className="h-9 w-[220px]"
                        placeholder="path.to.field"
                        value={jsonPathInput}
                        onChange={(event) => setJsonPathInput(event.target.value)}
                    />
                    <Input
                        aria-label="JSON value"
                        className="h-9 w-[220px]"
                        placeholder="value"
                        value={jsonValueInput}
                        onChange={(event) => setJsonValueInput(event.target.value)}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addJsonPathPredicate}
                        disabled={selectedJsonDescriptor === undefined}
                    >
                        Add JSON Filter
                    </Button>
                    <div className="flex flex-wrap gap-2">
                        {jsonPathPredicates.map((predicate, index) => (
                            <PredicateChip
                                key={`${predicate.field}:${predicate.path.join('.')}:${index.toString()}`}
                                label={`${predicate.field}.${predicate.path.join('.')}: ${JSON.stringify(predicate.value)}`}
                                removeLabel={`Remove ${predicate.field} JSON filter`}
                                onRemove={() => removeJsonPathPredicate(index)}
                            />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
